<template>
  <v-menu
    :model-value="openMenu"
    @update:modelValue="onMenuChange"
    max-width="400"
    location="right center"
    :close-on-content-click="false"
    @click:outside="openMenu = false"
  >
    <template v-slot:activator="{ props: activatorProps }">
      <div v-bind="activatorProps" @click="openMenu = !openMenu">
        <slot></slot>
      </div>
    </template>
    <template v-slot:default>
      <v-card class="ml-2 pa-3" width="300">
        <div class="d-flex pb-4 justify-space-between">
          <v-btn
            :class="{ 'icon-hidden': !openCreateStep }"
            variant="text"
            size="small"
            icon="mdi-arrow-left"
            @click="openCreateStep = false"
          />
          <span class="text-h7 font-weight-bold pt-2">Entities</span>
          <v-btn
            variant="text"
            size="small"
            icon="mdi-close"
            @click="openMenu = false"
          />
        </div>
        <v-text-field
          v-if="!store.state.isWorkflowDisabled"
          :rules="[maxNameLength]"
          class="select-entity"
          autofocus
          density="compact"
          v-model="search"
          :placeholder="openCreateStep ? 'Name the entity' : 'Search for entity'"
          variant="outlined"
          data-test="select-entity-search"
        />
        <v-textarea
          v-if="openCreateStep && !store.state.isWorkflowDisabled"
          auto-grow
          rows="3"
          class="no-border"
          variant="outlined"
          v-model="aiDescription"
          placeholder="Description (optional)"
        />

        <v-list>
          <template v-for="(entity) in filteredEntities">
            <v-list-item
              v-if='!openCreateStep && entity.id !== selectedEntity?.id'
              density="compact"
              :key="entity.id"
              @click="onSelectEntity(entity)"
              :disabled="store.state.isWorkflowDisabled"
              class="entity-row"
            >
              <v-list-item-title class="d-flex align-center justify-space-between">
                {{ entity.name }}
                <div class="d-flex align-center ga-1 entity-row-actions">
                  <edit-entity :entity="entity" />
                  <v-progress-circular
                    v-if="deleteEntityLoading"
                    indeterminate
                    size="16"
                    width="2"
                  />
                  <v-tooltip v-else location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="16"
                        color="grey"
                        class="cursor-pointer"
                        @click.stop
                        @click="() => onDeleteEntity(entity)"
                        loading
                      >
                        mdi-delete
                      </v-icon>
                    </template>
                    Delete
                  </v-tooltip>
                </div>
              </v-list-item-title>
            </v-list-item>
          </template>
          <v-divider class="my-2"></v-divider>
          <v-list-item
            density="compact"
            @click="onCreateEntity"
            :disabled="store.state.isWorkflowDisabled || hasMatchingEntity || (openCreateStep && !search)"
          >
            <v-list-item-title class="d-flex align-center ga-2">
              {{ openCreateStep ? 'Create' : 'Create a new entity with AI' }}
              <v-icon color="#787878">mdi-creation</v-icon>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </template>
  </v-menu>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import EditEntity from '@/components/EditEntity.vue'
import useEntityActions from '@/composables/useEntityActions'
import useGenerateEntity from '@/composables/useGenerateEntity'

const { entityId, name } = defineProps({ entityId: String, name: String })
const emit = defineEmits(['onSelectEntity'])

const openMenu = ref(false)
const openCreateStep = ref(false)
const deleteEntityLoading = ref(false)
const search = ref(name || '')
const aiDescription = ref('')
const { generateEntity } = useGenerateEntity()
const { deleteEntity } = useEntityActions()

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

const selectedEntity = computed(() => {
  return (workflow.value.dataModels ?? []).find((entity) => entity.id === entityId)
})

const filteredEntities = computed(() => {
  return (workflow.value.dataModels ?? []).filter((entity) => entity.name.toLowerCase().includes(search.value.toLowerCase()))
})
const hasMatchingEntity = computed(() => {
  return (workflow.value.dataModels ?? []).some((entity) => entity.name.toLowerCase() === search.value.toLowerCase())
})

const onCreateEntity = async () => {
  if (!openCreateStep.value) {
    openCreateStep.value = true
    return
  }

  const newEntity = await generateEntity({
    name: search.value.trim(),
    description: aiDescription.value.trim()
  })

  if (newEntity?.name) {
    search.value = newEntity.name
    openCreateStep.value = false
  }
}

const maxNameLength = (name) => {
  return name.length <= 30 || 'Name must be less than 30 characters'
}

const onSelectEntity = async (entity) => {
  emit('onSelectEntity', entity)
  openMenu.value = false
}

const onMenuChange = (isOpen) => {
  if (!isOpen) {
    setTimeout(() => {
      // Timeout for the menu to finish close animation before resetting
      // the search value (v-menu does not support natively)
      search.value = name || ''
      aiDescription.value = ''
      openCreateStep.value = false
    }, 250)
  }
}

const onDeleteEntity = async (entity) => {
  if (deleteEntityLoading.value) {
    return
  }

  deleteEntityLoading.value = true
  const index = (workflow.value.dataModels ?? []).findIndex((item) => item.id === entity.id)

  if (index > -1) {
    await deleteEntity(index)
  }
  deleteEntityLoading.value = false
}

watch(() => name, (newValue) => {
  if (newValue !== undefined) {
    search.value = newValue
  }
})
</script>

<style scoped>
::v-deep(.v-chip) {
  .v-chip__content {
    font-size: 12px;
  }

  .remove-entity-icon {
    font-size: 0 !important;
    width: 0 !important;
    transition: all 0.2s;
  }

  &:hover {
    .remove-entity-icon {
      font-size: 16px !important;
      width: 16px !important;
    }
  }
}

.icon-hidden {
  visibility: hidden;
}

.entity-row {
  .entity-row-actions {
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    .entity-row-actions {
      opacity: 1;
    }
  }
}
</style>
